var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useIntl } from "react-intl";
import { Button } from "@folio/stripes/components";
import { formatTitleWithParts } from "../utils/formatTitleWithParts";
import { formatEditionLabel } from "../utils/formatEditionLabel";
import { useOnixCodetableByNumber } from "../api/onixService";
import { ONIX_TABLES } from "../utils/onixTables";
import { onixLabel } from "../utils/onixLabel";
import { useCreateProductConfirm } from "./useCreateProductConfirm";
import { buildDateAuditColumns } from "../utils/dateAuditColumns";
var EDITION_COLUMN_WIDTH = 120;
var PUBLICATION_YEAR_COLUMN_WIDTH = 110;
export var useTitleTable = function (onRowClick, canCreateProduct, canCreateProductForItem) {
    if (canCreateProduct === void 0) { canCreateProduct = false; }
    if (canCreateProductForItem === void 0) { canCreateProductForItem = function () { return true; }; }
    var history = useHistory();
    var formatMessage = useIntl().formatMessage;
    var formTable = useOnixCodetableByNumber(ONIX_TABLES.PRODUCT_FORM).data;
    var openCreateProduct = useCallback(function (titleId) {
        return history.push("/bragency/titles/edit/".concat(titleId, "?newProduct=1"));
    }, [history]);
    // Shared with the list's actions menu (TitleList) so every "Vytvořit
    // produkt" on the list asks before creating another product.
    var _a = useCreateProductConfirm(openCreateProduct), requestCreateProduct = _a.requestCreate, createProductConfirmModalProps = _a.modalProps;
    var columns = useMemo(function () {
        var definedColumns = __spreadArray([
            {
                name: formatMessage({ id: "ui-br-agency.title.table.columns.name" }),
                key: "mainTitle",
                sortable: true,
                clickable: true,
                onClick: onRowClick,
                cellRenderer: function (item) { return formatTitleWithParts(item); },
            },
            {
                name: formatMessage({
                    id: "ui-br-agency.title.table.columns.contributor",
                }),
                key: "firstContributor",
                sortable: true,
            },
            {
                name: formatMessage({
                    id: "ui-br-agency.title.table.columns.publisher",
                }),
                key: "publisher",
                sortable: true,
            },
            {
                name: formatMessage({
                    id: "ui-br-agency.title.table.columns.editionNumber",
                }),
                key: "editionNumber",
                sortable: true,
                width: EDITION_COLUMN_WIDTH,
                cellRenderer: function (item) { return formatEditionLabel(item); },
            },
            {
                name: formatMessage({
                    id: "ui-br-agency.title.table.columns.publicationYear",
                }),
                key: "publicationYear",
                sortable: true,
                width: PUBLICATION_YEAR_COLUMN_WIDTH,
            },
            {
                name: formatMessage({
                    id: "ui-br-agency.title.table.columns.products",
                }),
                key: "products",
                cellRenderer: function (item) {
                    var _a;
                    var groups = [];
                    ((_a = item.products) !== null && _a !== void 0 ? _a : []).forEach(function (product) {
                        var label = onixLabel(formTable, product.form);
                        var existing = groups.find(function (group) { return group.label === label; });
                        if (existing) {
                            existing.count += 1;
                        }
                        else {
                            groups.push({ label: label, firstProductId: product.id, count: 1 });
                        }
                    });
                    return (React.createElement(React.Fragment, null, groups.map(function (group) {
                        var _a;
                        return (React.createElement(Button, { key: (_a = group.firstProductId) !== null && _a !== void 0 ? _a : group.label, buttonStyle: "default", marginBottom0: true, onClick: function () {
                                return history.push("/bragency/products/detail/".concat(group.firstProductId));
                            } }, group.count > 1
                            ? "".concat(group.label, " (").concat(group.count, "\u00D7)")
                            : group.label));
                    })));
                },
            }
        ], buildDateAuditColumns(formatMessage, "ui-br-agency.title.table.columns"), true);
        if (canCreateProduct) {
            definedColumns.push({
                name: "",
                key: "id",
                id: "actions",
                cellRenderer: function (item) {
                    // A publisher-only user only gets this quick action on titles they
                    // own — never on a title where they are merely a co-publisher.
                    if (!canCreateProductForItem(item)) {
                        return null;
                    }
                    return (React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, onClick: function () {
                            var _a;
                            // The search index can lag behind a just-created product: trust
                            // it only when it already lists products, otherwise let the
                            // hook check the live ["products", id] query.
                            return requestCreateProduct(item.id, ((_a = item.products) === null || _a === void 0 ? void 0 : _a.length) ? true : undefined);
                        } }, formatMessage({
                        id: "ui-br-agency.title.action.createProduct",
                    })));
                },
            });
        }
        return definedColumns;
    }, [
        formatMessage,
        onRowClick,
        canCreateProduct,
        canCreateProductForItem,
        history,
        formTable,
        requestCreateProduct,
    ]);
    return { columns: columns, requestCreateProduct: requestCreateProduct, createProductConfirmModalProps: createProductConfirmModalProps };
};
